/** Same fill as `Button` `variant="primary"`. */
const BUTTON_PRIMARY_GRADIENT =
  'bg-[linear-gradient(135deg,#00C2C2_0%,#155DFC_100%)]';

/** Nav / submenu row hover background (Figma). */
export const SIDEBAR_ITEM_EXPANDED_HOVER_BG = '#2A2A2A';
export const sidebarItemHoverBgClass = 'hover:bg-[#2A2A2A]';
export const sidebarItemExpandedRowHoverClass = `rounded-lg ${sidebarItemHoverBgClass}`;

/**
 * Skeleton shimmer override — adapts the design-system Skeleton component's default
 * light-mode gradient to match the sidebar panel background.
 *
 * Dark panels (default / primary) are always dark regardless of OS dark-mode,
 * so we override with a white-translucent shimmer in both light and dark modes.
 *
 * Frost panel is white in light mode (use a subtle dark shimmer) and dark in dark mode
 * (use white-translucent shimmer via dark: prefix).
 */
const SKELETON_CLASS_DARK =
  '!bg-[linear-gradient(90deg,rgba(255,255,255,0.07)_0px,rgba(255,255,255,0.14)_40px,rgba(255,255,255,0.07)_80px)] dark:!bg-[linear-gradient(90deg,rgba(255,255,255,0.07)_0px,rgba(255,255,255,0.14)_40px,rgba(255,255,255,0.07)_80px)]';

const SKELETON_CLASS_FROST =
  '!bg-[linear-gradient(90deg,rgba(0,0,0,0.05)_0px,rgba(0,0,0,0.10)_40px,rgba(0,0,0,0.05)_80px)] dark:!bg-[linear-gradient(90deg,rgba(255,255,255,0.07)_0px,rgba(255,255,255,0.14)_40px,rgba(255,255,255,0.07)_80px)]';

/** Shared dark panel tokens used by `default` and `primary` variants. */
const DARK_PANEL = {
  panelBg: 'bg-black dark:bg-[#0F172B]',
  panelShadow: '',
  labelInactive: 'text-white/70',
  labelInactiveHover: 'group-hover:text-white',
  labelActive: 'text-white',
  rowHoverBg: 'hover:bg-[#2A2A2A]',
  collapsedIconHover: 'group-hover:bg-white/5 group-hover:text-white',
  toggleText: 'text-white',
  toggleHover: 'hover:bg-white/5 hover:text-white',
  submenuBg: 'bg-black dark:bg-[#0F172B]',
  submenuShadow: 'shadow-[8px_8px_8px_rgba(0,0,0,0.08)]',
  submenuTextInactive: 'text-[#f2f2f2]',
  accordionTextInactive: 'text-white/60',
  footerBorder: 'border-white/10',
  scrollbarThumb: 'scrollbar-thumb-white/20',
  skeletonClass: SKELETON_CLASS_DARK,
};

/** Shared frost panel tokens — white in light mode, dark in dark mode. */
const FROST_PANEL = {
  panelBg: 'bg-white dark:bg-[#0F172B]',
  panelShadow: 'shadow-[0_8px_32px_rgba(0,0,0,0.10),0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-none',
  labelInactive: 'text-[#4B5563] dark:text-white/70',
  labelInactiveHover: 'group-hover:text-[#111827] dark:group-hover:text-white',
  labelActive: 'text-[#111827] dark:text-white',
  rowHoverBg: 'hover:bg-[#F3F4F6] dark:hover:bg-[#2A2A2A]',
  collapsedIconHover: 'group-hover:bg-[#F3F4F6] group-hover:text-[#111827] dark:group-hover:bg-white/5 dark:group-hover:text-white',
  toggleText: 'text-[#374151] dark:text-white',
  toggleHover: 'hover:bg-[#F3F4F6] hover:text-[#111827] dark:hover:bg-white/5 dark:hover:text-white',
  submenuBg: 'bg-white dark:bg-[#0F172B]',
  submenuShadow: 'shadow-[8px_8px_24px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[8px_8px_8px_rgba(0,0,0,0.08)]',
  submenuTextInactive: 'text-[#374151] dark:text-[#f2f2f2]',
  accordionTextInactive: 'text-[#6B7280] dark:text-white/60',
  footerBorder: 'border-black/10 dark:border-white/10',
  scrollbarThumb: 'scrollbar-thumb-black/10 dark:scrollbar-thumb-white/20',
  skeletonClass: SKELETON_CLASS_FROST,
};

/**
 * Sidebar color variants — accent + panel color scheme.
 * `default`: teal→blue gradient active; black panel.
 * `primary`: purple gradient active; black panel.
 * `frost`: teal→blue gradient active; white panel in light mode, dark panel in dark mode.
 */
/**
 * Active submenu item: gradient left border (2px) + subtle bg highlight.
 * Uses ::before pseudo-element positioned within the button (button must have position:relative).
 */
const SUBMENU_ACTIVE_DARK = [
  'relative bg-white/10 text-white',
  "before:content-[''] before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:rounded-full",
  'before:bg-[linear-gradient(180deg,#00C2C2_0%,#155DFC_100%)]',
].join(' ');

const SUBMENU_ACTIVE_PURPLE = [
  'relative bg-white/10 text-white',
  "before:content-[''] before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:rounded-full",
  'before:bg-[linear-gradient(180deg,#7c5cfc_0%,#5b8def_100%)]',
].join(' ');

const SUBMENU_ACTIVE_FROST = [
  'relative bg-black/5 dark:bg-white/10 text-[#111827] dark:text-white',
  "before:content-[''] before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:rounded-full",
  'before:bg-[linear-gradient(180deg,#00C2C2_0%,#155DFC_100%)]',
].join(' ');

export const SIDEBAR_VARIANTS = {
  default: {
    ...DARK_PANEL,
    iconInactive: 'text-[#80AFFF]',
    iconChevron: 'text-[#80AFFF]',
    iconActive: [
      BUTTON_PRIMARY_GRADIENT,
      'text-white shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    ].join(' '),
    iconHoverBorder: 'group-hover:border-[#155DFC]',
    iconHoverShadow: 'group-hover:shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    toggleHoverBorder: 'hover:border-[#155DFC]',
    toggleHoverShadow: 'hover:shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    toggleFocusRing: 'focus-visible:ring-[#155DFC]/50',
    itemFocusRing: 'focus-visible:ring-[#155DFC]/50',
    submenuActive: SUBMENU_ACTIVE_DARK,
    submenuFocusRing: 'focus-visible:ring-[#155DFC]/50',
  },
  primary: {
    ...DARK_PANEL,
    iconInactive: 'text-white',
    iconChevron: 'text-white',
    iconActive:
      'bg-[linear-gradient(135deg,#7c5cfc,#5b8def)] text-white shadow-[4px_8px_20px_rgba(124,92,252,0.35)]',
    iconHoverBorder: 'group-hover:border-[#7c5cfc]',
    iconHoverShadow: 'group-hover:shadow-[4px_8px_20px_rgba(124,92,252,0.35)]',
    toggleHoverBorder: 'hover:border-[#7c5cfc]',
    toggleHoverShadow: 'hover:shadow-[4px_8px_20px_rgba(124,92,252,0.35)]',
    toggleFocusRing: 'focus-visible:ring-[#7c5cfc]/50',
    itemFocusRing: 'focus-visible:ring-[#7c5cfc]/50',
    submenuActive: SUBMENU_ACTIVE_PURPLE,
    submenuFocusRing: 'focus-visible:ring-[#7c5cfc]/50',
  },
  frost: {
    ...FROST_PANEL,
    iconInactive: 'text-[#80AFFF]',
    iconChevron: 'text-[#6B7280] dark:text-[#80AFFF]',
    iconActive: [
      BUTTON_PRIMARY_GRADIENT,
      'text-white shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    ].join(' '),
    iconHoverBorder: 'group-hover:border-[#155DFC]',
    iconHoverShadow: 'group-hover:shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    toggleHoverBorder: 'hover:border-[#155DFC]',
    toggleHoverShadow: 'hover:shadow-[4px_8px_20px_rgba(21,93,252,0.35)]',
    toggleFocusRing: 'focus-visible:ring-[#155DFC]/50',
    itemFocusRing: 'focus-visible:ring-[#155DFC]/50',
    submenuActive: SUBMENU_ACTIVE_FROST,
    submenuFocusRing: 'focus-visible:ring-[#155DFC]/50',
  },
};

const sidebarTooltipBase = {
  arrow: false,
  align: 'start',
};

/** Toggle expand/collapse: debajo del botón. */
export const sidebarToggleTooltipProps = {
  ...sidebarTooltipBase,
  position: 'bottom',
};

/** Ítems en sidebar colapsado: encima del icono. */
export const sidebarItemTooltipProps = {
  ...sidebarTooltipBase,
  position: 'top',
};

/** @param {'default' | 'primary' | 'frost'} variant */
export function resolveSidebarVariant(variant) {
  return SIDEBAR_VARIANTS[variant] ?? SIDEBAR_VARIANTS.default;
}
